import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, CheckCircle, Phone, Buildings, House, Wrench } from "@phosphor-icons/react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { BrandMarquee } from "@/components/site/Marquee";
import { SERVICES, FACILITY, BUSINESS } from "@/lib/data";

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 180);
        return () => clearTimeout(t);
      }
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      <PageHero
        testId="services-hero"
        eyebrow="Our Services"
        lines={["Everything", "around the house."]}
        subtitle={`Quality work with flexibility across ${BUSINESS.area} — for homes and facilities alike. If it needs doing, there's a good chance we do it.`}
      />

      {/* Category switcher */}
      <div className="bg-white border-b border-slate-200 sticky top-[72px] z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-3 flex flex-wrap gap-2">
          <a href="#residential" data-testid="jump-residential" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2 text-sm font-bold text-navy-900 hover:bg-navy-900 hover:text-white transition-colors">
            <House weight="fill" size={16} /> Residential Services
          </a>
          <a href="#facility" data-testid="jump-facility" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2 text-sm font-bold text-navy-900 hover:bg-navy-900 hover:text-white transition-colors">
            <Buildings weight="fill" size={16} /> Facility Maintenance Solutions
          </a>
        </div>
      </div>

      <BrandMarquee dark={false} />

      {/* RESIDENTIAL */}
      <section id="residential" data-testid="residential-section" className="scroll-mt-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-20 md:pt-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <House size={24} weight="fill" className="text-navy-600" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">For your home</span>
            </div>
            <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tight text-navy-900 leading-[1.1]">
              Residential Services
            </h2>
          </Reveal>
        </div>

        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20 space-y-24 md:space-y-32">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug}>
              <div id={s.slug} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="relative rounded-3xl overflow-hidden group bg-slate-100">
                    <img
                      src={s.image}
                      alt={s.title}
                      className={`w-full h-[380px] md:h-[480px] transition-transform duration-700 group-hover:scale-105 ${
                        s.slug === "fence-installation" ? "object-contain" : "object-cover"
                      }`}
                    />
                    <span className="absolute top-5 left-5 rounded-full bg-white/90 backdrop-blur px-4 py-1.5 text-sm font-bold text-navy-900">
                      0{i + 1}
                    </span>
                  </div>
                </div>
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-600">{s.tagline}</span>
                  <h3 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-navy-900 mt-3 leading-[1.1]">
                    {s.title}
                  </h3>
                  <p className="text-slate-600 mt-5 text-lg leading-relaxed">{s.desc}</p>
                  <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2.5 text-navy-900 font-medium">
                        <CheckCircle size={20} weight="fill" className="text-navy-600 shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact-us"
                    data-testid={`service-quote-${s.slug}`}
                    className="group inline-flex items-center gap-2 mt-8 rounded-full bg-navy-900 text-white px-6 py-3.5 font-bold hover:bg-navy-600 transition-colors"
                  >
                    Get a quote for this
                    <ArrowUpRight weight="bold" className="group-hover:rotate-45 transition-transform" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FACILITY */}
      <section id="facility" data-testid="facility-section" className="scroll-mt-32 bg-navy-900 text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <Buildings size={24} weight="fill" className="text-navy-400" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-400">For commercial & rental properties</span>
            </div>
            <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tight leading-[1.1]">
              Facility Maintenance Solutions
            </h2>
            <p className="text-slate-300 mt-6 max-w-2xl text-lg">
              Dependable, professional upkeep for property managers, landlords, offices, and retail spaces —
              the same trusted quality, scaled for your facility.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {FACILITY.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-2xl border border-white/10 bg-navy-800/50 p-7 hover:bg-navy-800 transition-colors">
                  <span className="w-11 h-11 grid place-items-center rounded-full bg-white/10 mb-5">
                    <Wrench size={20} weight="fill" className="text-white" />
                  </span>
                  <h3 className="font-display font-bold text-xl tracking-tight">{f.title}</h3>
                  <p className="text-slate-400 mt-3 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-14">
              <Link
                to="/contact-us"
                data-testid="facility-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white text-navy-900 px-8 py-4 font-bold hover:bg-slate-100 transition-colors"
              >
                Discuss a facility plan <ArrowUpRight weight="bold" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 text-center">
          <Reveal>
            <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tighter leading-[1.05] text-navy-900">
              Have a project in mind?
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-8 py-4 font-bold hover:bg-navy-600 transition-colors">
                Request a Quote <ArrowUpRight weight="bold" />
              </Link>
              <a href={BUSINESS.phoneHref} className="inline-flex items-center gap-2 rounded-full border-2 border-navy-900 text-navy-900 px-8 py-4 font-bold hover:bg-navy-900 hover:text-white transition-colors">
                <Phone weight="fill" /> {BUSINESS.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
